import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, Text } from '~/components/core';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_S, FONT_SIZE_XS, FONT_SIZE_XXXXL } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { resultHeadline, resultSummaryLine, StrategyResultView } from '../../utils/results';

type Props = {
    results: StrategyResultView;
    onEnable: () => void;
    onDiscard: () => void;
    busy?: boolean;
};

// The decision moment for done/seen strategies: headline backtest numbers + the
// enable-or-discard choice. Tabs below let the user inspect before deciding.
export default function DecisionPanel({ results, onEnable, onDiscard, busy }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const summaryLine = resultSummaryLine(results);

    return (
        <View style={styles.container}>
            <Text style={styles.caption}>ai-strategy-decision-return</Text>
            <Text noTranslate fontType="LATO_BOLD" style={styles.headline}>
                {resultHeadline(results)}
            </Text>
            <Text noTranslate style={styles.summaryLine}>
                {summaryLine}
            </Text>
            <View style={styles.buttonsRow}>
                <View style={styles.buttonWrap}>
                    <Button
                        title="ai-strategy-action-discard"
                        onPress={onDiscard}
                        disabled={busy}
                        backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                        titleColor={theme.text}
                    />
                </View>
                <View style={[styles.buttonWrap, styles.buttonWrapPrimary]}>
                    <Button title="ai-strategy-enable-cta" onPress={onEnable} disabled={busy} />
                </View>
            </View>
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            alignItems: 'center',
            paddingVertical: scaleSize(14),
            paddingHorizontal: scaleSize(14),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: theme.tabIconDefault,
        },
        caption: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
        },
        headline: {
            fontSize: FONT_SIZE_XXXXL,
            color: theme.button,
            marginTop: scaleSize(2),
        },
        summaryLine: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginTop: scaleSize(4),
            textAlign: 'center',
        },
        buttonsRow: {
            flexDirection: 'row',
            marginTop: scaleSize(12),
            alignSelf: 'stretch',
        },
        buttonWrap: {
            flex: 1,
            marginHorizontal: scaleSize(4),
        },
        buttonWrapPrimary: {
            flex: 2,
        },
    });
